import { Table } from '@backstage/core-components'
import { AwsLambda } from '@internal/backstage-plugin-veho-backstage-common'
import { Card, CardContent, Chip, Drawer, Grid, IconButton, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 700,
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  envTable: {
    maxHeight: 350,
  },
  arnText: {
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
}))

export interface LambdaDetailsDrawerProps {
  lambda: AwsLambda | null
  open: boolean
  onClose: () => void
}

const LambdaEnvironmentDetails = ({ className, environment }: { className: string; environment?: string[] }) => {
  return (
    <div className={className}>
      <Typography variant="h6" gutterBottom>
        Environment Variables
      </Typography>
      <Table
        title="Environment Variables"
        options={{ search: false, paging: false, toolbar: false }}
        columns={[{ title: 'Name', field: 'name' }]}
        data={environment?.map(name => ({ name })) ?? []}
      />
    </div>
  )
}

const LambdaTagsDetails = ({ className, tags }: { className: string; tags?: Record<string, string> }) => (
  <div className={className}>
    <Typography variant="h6" gutterBottom>
      Tags
    </Typography>
    <Table
      title="Tags"
      options={{ search: false, paging: false, toolbar: false }}
      columns={[
        { title: 'Tag', field: 'key' },
        { title: 'Value', field: 'value' },
      ]}
      data={tags ? Object.entries(tags).map(([key, value]) => ({ key, value })) : []}
    />
  </div>
)

export const LambdaDetailsDrawer = ({ lambda, open, onClose }: LambdaDetailsDrawerProps) => {
  const classes = useStyles()

  if (!lambda) {
    return null
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className={classes.drawer}>
        <div className={classes.header}>
          <Typography variant="h6" component="h2">
            {lambda.functionName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.section}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ARN
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {lambda.arn}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Runtime
                  </Typography>
                  <Typography variant="body1">{lambda.runtime}</Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Architecture
                  </Typography>
                  <Typography variant="body1">{lambda.architecture}</Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Memory Size
                  </Typography>
                  <Typography variant="body1">{lambda.memorySize} MB</Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    State
                  </Typography>
                  <Chip label={lambda.state} size="small" color={lambda.state === 'Active' ? 'primary' : 'default'} />
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Update Status
                  </Typography>
                  <Chip
                    label={lambda.lastUpdateStatus}
                    size="small"
                    color={lambda.lastUpdateStatus === 'Successful' ? 'primary' : 'secondary'}
                  />
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Concurrent Executions
                  </Typography>
                  <Typography variant="body1">
                    {lambda.concurrentExecutions > 0 ? lambda.concurrentExecutions : 'Unrestricted'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
        <LambdaEnvironmentDetails className={classes.section} environment={lambda.environment} />
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            Layers
          </Typography>
          <Table
            title="Layers"
            options={{ search: false, paging: false, toolbar: false }}
            columns={[{ title: 'ARN', field: 'arn' }]}
            data={lambda.layers.map(layerArn => ({ arn: layerArn }))}
          />
        </div>
        <LambdaTagsDetails className={classes.section} tags={lambda.tags} />
      </div>
    </Drawer>
  )
}
